// Public mentor profile (PRD §19.8) — UNAUTHENTICATED.
// Renders only when the mentor is active AND has explicitly opted in.
// Backend returns 404 (not 403) when not opted in so we do not confirm
// existence to a stranger.
import { useEffect, useState } from "react";
import { useRoute } from "wouter";
import { Loader2, MapPin, ExternalLink, Sparkles } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

interface PublicMentor {
  id: number;
  fullName: string;
  headline: string | null;
  bio: string | null;
  location: string | null;
  linkedinUrl: string | null;
  expertiseTags: string[];
  depth: Array<{ tag: string; description: string }>;
}

// Architect-flagged: a public page renders user-provided URL fields. We
// must validate scheme — a `javascript:` URL on an <a href> would execute
// in the visitor's context. Whitelist http/https only and drop anything
// else silently (better than rendering a broken/dangerous link).
function safeHttpUrl(u: string | null): string | null {
  if (!u) return null;
  try {
    const parsed = new URL(u);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") return parsed.toString();
    return null;
  } catch { return null; }
}

export default function MentorPublicProfile() {
  const [, params] = useRoute("/m/:id");
  const id = params?.id;
  const [mentor, setMentor] = useState<PublicMentor | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    fetch(`${BASE}api/public/mentors/${id}`)
      .then(r => {
        if (r.ok) return r.json();
        // Distinguish "not opted in / does not exist" (404) from transient
        // server failures so the visitor sees the right message.
        return Promise.reject(r.status === 404 ? "not-found" : "server-error");
      })
      .then(d => setMentor(d.mentor))
      .catch(e => setErr(String(e)))
      .finally(() => setLoading(false));
  }, [id]);

  // Set page title for browser tab + link previews.
  useEffect(() => {
    if (mentor?.fullName) document.title = `${mentor.fullName} · Favcy Select Mentor`;
    return () => { document.title = "Favcy Select"; };
  }, [mentor?.fullName]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b0b0c] text-white flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-white/60" />
      </div>
    );
  }

  if (err === "server-error") {
    return (
      <div className="min-h-screen bg-[#0b0b0c] text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="text-base font-semibold mb-2">We could not load this profile right now</div>
          <div className="text-sm text-white/60">Please try again in a moment.</div>
        </div>
      </div>
    );
  }
  if (err === "not-found" || !mentor) {
    return (
      <div className="min-h-screen bg-[#0b0b0c] text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="text-base font-semibold mb-2">This profile is not available</div>
          <div className="text-sm text-white/60">The mentor may have made it private, or the link may be incorrect.</div>
        </div>
      </div>
    );
  }

  const safeLinkedin = safeHttpUrl(mentor.linkedinUrl);

  return (
    <div className="min-h-screen bg-[#0b0b0c] text-white">
      <header className="border-b border-white/10 sticky top-0 z-20 bg-[#0b0b0c]/90 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="text-sm font-bold tracking-tight">Favcy Select · Mentor</div>
          <a href="/mentors/apply" className="text-xs text-white/60 hover:text-white">Apply to mentor</a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-8">
        <section>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="text-mentor-name">{mentor.fullName}</h1>
          {mentor.headline && (
            <div className="text-base text-white/80 mt-1.5" data-testid="text-mentor-headline">{mentor.headline}</div>
          )}
          <div className="flex flex-wrap items-center gap-3 text-xs text-white/55 mt-3">
            {mentor.location && (
              <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {mentor.location}</span>
            )}
            {safeLinkedin && (
              <a href={safeLinkedin} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 hover:text-white" data-testid="link-mentor-linkedin">
                <ExternalLink className="w-3 h-3" /> LinkedIn
              </a>
            )}
          </div>
        </section>

        {mentor.bio && (
          <section>
            <h2 className="text-sm font-semibold text-white/80 mb-2">About</h2>
            <p className="text-sm text-white/75 leading-relaxed whitespace-pre-line" data-testid="text-mentor-bio">{mentor.bio}</p>
          </section>
        )}

        {mentor.expertiseTags.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold text-white/80 mb-2">Expertise</h2>
            <div className="flex flex-wrap gap-2">
              {mentor.expertiseTags.map((t) => (
                <span key={t} className="text-xs px-2.5 py-1 rounded-full border border-[#FF5C00]/30 text-[#FF5C00] bg-[#FF5C00]/5" data-testid={`tag-${t}`}>{t}</span>
              ))}
            </div>
          </section>
        )}

        {mentor.depth.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold text-white/80 mb-3">What I have actually done</h2>
            <div className="space-y-3">
              {mentor.depth.map((d, i) => (
                <div key={i} className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
                  <div className="text-xs font-semibold text-[#FF5C00] mb-1 inline-flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> {d.tag}
                  </div>
                  <div className="text-sm text-white/75 leading-relaxed">{d.description}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="border-t border-white/10 pt-6 text-xs text-white/50">
          This is a Favcy Select mentor profile. Companies in our portfolio can request an intro through the platform.
        </section>
      </main>
    </div>
  );
}
